/* GTJ Journey - first-party browsing personalization.
   Tracks product views in localStorage (key: gtj-journey), builds a taste
   profile (type / tags / collections, recency-decayed), then:
   1. re-ranks "You may also like" (product-recommendations) grids
   2. picks the best "Complete The Look" pair in the cart drawer
   3. renders the homepage "Recently viewed" section
   Signals: view = 1, wishlist save = +2 (read from gtj-wishlist), add to
   cart = +3 (fetch intercept on /cart/add). Price-band affinity bonus and
   a diversity guard (one top slot reserved for a different product type).
   Debug: add ?gtj-debug to any URL to see the profile + per-card scores.
   No regexes, no :has(), no external calls. */
(function () {
  'use strict';

  var KEY = 'gtj-journey';
  var MAX_VIEWS = 40;
  var HALF_LIFE_DAYS = 7;
  var ATC_WEIGHT = 3;
  var WISHLIST_WEIGHT = 2;
  var DEBUG = window.location.search.indexOf('gtj-debug') !== -1;

  /* ---------- storage ---------- */

  function load() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return { v: 1, views: [] };
      var data = JSON.parse(raw);
      if (!data || !Array.isArray(data.views)) return { v: 1, views: [] };
      return data;
    } catch (e) {
      return { v: 1, views: [] };
    }
  }

  function save(data) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) {
      /* storage full or blocked - personalization simply stays off */
    }
  }

  function wishlistHandles() {
    var map = {};
    try {
      var raw = window.localStorage.getItem('gtj-wishlist');
      if (!raw) return map;
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return map;
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        if (typeof it === 'string') map[it] = true;
        else if (it && it.handle) map[it.handle] = true;
      }
    } catch (e) { /* noop */ }
    return map;
  }

  /* ---------- tracking ---------- */

  function upsertView(data, p, intentBonus) {
    if (!p || !p.handle) return;
    var found = null;
    for (var i = 0; i < data.views.length; i++) {
      if (data.views[i].h === p.handle) {
        found = data.views[i];
        break;
      }
    }
    if (found) {
      if (intentBonus) found.iw = (found.iw || 0) + intentBonus;
      else found.n = (found.n || 1) + 1;
      found.ts = Date.now();
      if (p.title) found.t = p.title;
      if (p.image) found.img = p.image;
      if (p.priceFormatted) found.pf = p.priceFormatted;
      if (p.quick) found.vs = p.quick;
    } else {
      data.views.push({
        h: p.handle,
        t: p.title || p.handle,
        u: p.url || ('/products/' + p.handle),
        img: p.image || '',
        price: p.price || 0,
        pf: p.priceFormatted || '',
        type: p.type || '',
        tags: p.tags || [],
        cols: p.collections || [],
        ts: Date.now(),
        n: intentBonus ? 0 : 1,
        iw: intentBonus || 0,
        vs: p.quick || null
      });
    }
    data.views.sort(function (a, b) { return b.ts - a.ts; });
    if (data.views.length > MAX_VIEWS) data.views.length = MAX_VIEWS;
    save(data);
  }

  function trackProductView() {
    var el = document.getElementById('gtj-journey-product');
    if (!el) return;
    var p;
    try {
      p = JSON.parse(el.textContent);
    } catch (e) {
      return;
    }
    upsertView(load(), p, 0);
  }

  /* Add-to-cart intent: intercept fetch to /cart/add - the response line
     item(s) carry handle / product_type / price, enough to strengthen or
     seed a profile entry even for quick-adds from collection pages. */
  function hookAddToCart() {
    if (!window.fetch) return;
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = '';
      try {
        url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
      } catch (e) { /* noop */ }
      var promise = origFetch.apply(this, arguments);
      if (url.indexOf('/cart/add') !== -1) {
        promise.then(function (res) {
          if (!res || !res.ok) return;
          try {
            res.clone().json().then(function (body) {
              var items = [];
              if (body && Array.isArray(body.items)) items = body.items;
              else if (body && body.handle) items = [body];
              for (var i = 0; i < items.length; i++) {
                var it = items[i];
                if (!it || !it.handle) continue;
                upsertView(load(), {
                  handle: it.handle,
                  title: it.product_title || it.title,
                  url: it.url,
                  image: it.image || it.featured_image && it.featured_image.url,
                  price: it.price,
                  type: it.product_type || '',
                  tags: [],
                  collections: []
                }, ATC_WEIGHT);
                debugLog('ATC intent +' + ATC_WEIGHT + ': ' + it.handle);
              }
            }).catch(function () { /* non-JSON response - ignore */ });
          } catch (e) { /* never break the actual add-to-cart */ }
        }).catch(function () { /* noop */ });
      }
      return promise;
    };
  }

  /* ---------- profile ---------- */

  function buildProfile(views) {
    var wl = wishlistHandles();
    var profile = { types: {}, tags: {}, cols: {}, count: views.length, medianPrice: 0 };
    var now = Date.now();
    var prices = [];
    for (var i = 0; i < views.length; i++) {
      var v = views[i];
      var ageDays = (now - v.ts) / 86400000;
      var intensity = (v.n || 0) + (v.iw || 0) + (wl[v.h] ? WISHLIST_WEIGHT : 0);
      var w = Math.pow(0.5, ageDays / HALF_LIFE_DAYS) * (1 + Math.log(1 + intensity));
      if (v.price > 0) prices.push(v.price);
      if (v.type) profile.types[v.type.toLowerCase()] = (profile.types[v.type.toLowerCase()] || 0) + w;
      var tags = v.tags || [];
      for (var j = 0; j < tags.length; j++) {
        var tg = String(tags[j]).toLowerCase();
        profile.tags[tg] = (profile.tags[tg] || 0) + w;
      }
      var cols = v.cols || [];
      for (var k = 0; k < cols.length; k++) {
        var c = String(cols[k]).toLowerCase();
        profile.cols[c] = (profile.cols[c] || 0) + w;
      }
    }
    if (prices.length) {
      prices.sort(function (a, b) { return a - b; });
      profile.medianPrice = prices[Math.floor(prices.length / 2)];
    }
    return profile;
  }

  /* Fresh context per personalization pass, so add-to-cart / wishlist
     signals recorded after page load influence the next drawer render. */
  function ctx() {
    var data = load();
    var profile = buildProfile(data.views);
    var viewed = {};
    for (var i = 0; i < data.views.length; i++) viewed[data.views[i].h] = true;
    return { data: data, profile: profile, viewed: viewed };
  }

  function priceBonus(profile, priceAttr) {
    if (!profile.medianPrice) return 0;
    var price = parseInt(priceAttr, 10);
    if (!price || price <= 0) return 0;
    var ratio = price > profile.medianPrice ? price / profile.medianPrice : profile.medianPrice / price;
    if (ratio <= 1.4) return 1.5;
    if (ratio <= 2) return 0.75;
    return 0;
  }

  function scoreEl(c, el) {
    var handle = el.getAttribute('data-gtj-handle');
    var type = el.getAttribute('data-gtj-type');
    var tagsCsv = el.getAttribute('data-gtj-tags');
    var score = 0;
    if (c.viewed[handle]) score += 1.5;
    if (type && c.profile.types[type.toLowerCase()]) {
      score += 3 * c.profile.types[type.toLowerCase()];
    }
    if (tagsCsv) {
      var parts = tagsCsv.split(',');
      var tagScore = 0;
      for (var i = 0; i < parts.length; i++) {
        var tg = parts[i].trim().toLowerCase();
        if (tg && c.profile.tags[tg]) tagScore += c.profile.tags[tg];
      }
      if (tagScore > 6) tagScore = 6;
      score += tagScore;
    }
    score += priceBonus(c.profile, el.getAttribute('data-gtj-price'));
    return Math.round(score * 100) / 100;
  }

  /* Product category: GTJ sets product.type to the material ("925 sterling
     silver") across rings/bracelets/pendants alike, so the real category is
     detected from tags. More specific stems first (earring before ring). */
  var CATEGORY_STEMS = ['earring', 'ring', 'bracelet', 'pendant', 'chain', 'necklace', 't-shirt', 'tshirt', 'polo', 'shirt', 'short', 'trouser', 'wallet', 'belt', 'duffle', 'bag'];

  function categoryOf(type, tagsCsv) {
    var hay = ((tagsCsv || '') + ',' + (type || '')).toLowerCase();
    for (var i = 0; i < CATEGORY_STEMS.length; i++) {
      if (hay.indexOf(CATEGORY_STEMS[i]) !== -1) return CATEGORY_STEMS[i];
    }
    return (type || '').toLowerCase();
  }

  /* Diversity guard: if every one of the first `span` slots shares one
     category, promote the best-scoring different-category item into the
     last of those slots so the row keeps a discovery element. */
  function applyDiversity(items, span) {
    if (items.length <= span) return items;
    var firstCat = items[0].cat || '';
    if (!firstCat) return items;
    for (var i = 1; i < span; i++) {
      if ((items[i].cat || '') !== firstCat) return items;
    }
    for (var j = span; j < items.length; j++) {
      if ((items[j].cat || '') !== firstCat && items[j].score > 0) {
        var pick = items.splice(j, 1)[0];
        items.splice(span - 1, 0, pick);
        return items;
      }
    }
    return items;
  }

  /* ---------- 1. re-rank recommendation grids ---------- */

  function rerankGrid(grid) {
    if (!grid || grid.getAttribute('data-gtj-ranked') === '1') return;
    var c = ctx();
    if (!c.data.views.length) return;
    var items = [];
    var children = grid.children;
    for (var i = 0; i < children.length; i++) {
      var wrapper = children[i].querySelector('[data-gtj-handle]');
      if (!wrapper) return; /* unexpected markup - leave untouched */
      items.push({
        el: children[i],
        order: i,
        cat: categoryOf(wrapper.getAttribute('data-gtj-type'), wrapper.getAttribute('data-gtj-tags')),
        score: scoreEl(c, wrapper)
      });
    }
    if (items.length < 2) return;
    var anyScore = false;
    for (var j = 0; j < items.length; j++) {
      if (items[j].score > 0) { anyScore = true; break; }
    }
    if (!anyScore) return;

    items.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.order - b.order;
    });
    items = applyDiversity(items, 4);
    grid.setAttribute('data-gtj-ranked', '1');
    for (var k = 0; k < items.length; k++) {
      grid.appendChild(items[k].el);
      if (DEBUG) debugBadge(items[k].el, items[k].score);
    }
    debugLog('Re-ranked recommendations (' + items.length + ' cards)');
  }

  function watchRecommendations() {
    var sections = document.querySelectorAll('product-recommendations');
    for (var i = 0; i < sections.length; i++) {
      (function (sec) {
        var existing = sec.querySelector('.grid');
        if (existing) rerankGrid(existing);
        var mo = new MutationObserver(function () {
          var grid = sec.querySelector('.grid');
          if (grid) rerankGrid(grid);
        });
        mo.observe(sec, { childList: true, subtree: true });
      })(sections[i]);
    }
  }

  /* ---------- 2. cart drawer Complete The Look ---------- */

  function personalizeCTL() {
    var grids = document.querySelectorAll('.gtj-ctl__grid');
    for (var g = 0; g < grids.length; g++) {
      var grid = grids[g];
      if (grid.getAttribute('data-gtj-ranked') === '1') continue;
      var cards = grid.querySelectorAll('a[data-gtj-handle]');
      if (cards.length < 3) continue;
      var c = ctx();
      if (!c.data.views.length) continue;
      var items = [];
      for (var i = 0; i < cards.length; i++) {
        items.push({
          el: cards[i],
          order: i,
          cat: categoryOf(cards[i].getAttribute('data-gtj-type'), cards[i].getAttribute('data-gtj-tags')),
          score: scoreEl(c, cards[i])
        });
      }
      var anyScore = false;
      for (var j = 0; j < items.length; j++) {
        if (items[j].score > 0) { anyScore = true; break; }
      }
      if (!anyScore) continue;
      items.sort(function (a, b) {
        if (b.score !== a.score) return b.score - a.score;
        return a.order - b.order;
      });
      /* two slots: best overall + best different-type (complete the look
         means complementary, not a twin of what is already in the cart) */
      items = applyDiversity(items, 2);
      grid.setAttribute('data-gtj-ranked', '1');
      for (var k = 0; k < items.length; k++) {
        if (k < 2) {
          items[k].el.removeAttribute('hidden');
          grid.appendChild(items[k].el);
          if (DEBUG) debugBadge(items[k].el, items[k].score);
        } else {
          items[k].el.setAttribute('hidden', '');
        }
      }
      debugLog('Personalized Complete The Look');
    }
  }

  function watchCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    personalizeCTL();
    var mo = new MutationObserver(function () {
      personalizeCTL();
    });
    mo.observe(drawer, { childList: true, subtree: true });
  }

  /* ---------- 3. Recently viewed row (product pages) ---------- */

  /* Same SVG markup as gtj-card-extras.js so the delegated wishlist
     handler and the is-saved CSS states apply to these buttons too. */
  var BM_OUTLINE = '<svg class="gtj-bookmark-outline" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1h12v16l-6-4.5L1 17V1z" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>';
  var BM_FILL = '<svg class="gtj-bookmark-fill" viewBox="0 0 14 18" xmlns="http://www.w3.org/2000/svg"><path d="M1 1h12v16l-6-4.5L1 17V1z" fill="currentColor"/></svg>';
  var PLUS_SVG = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="1.5"/></svg>';

  function fmtMoney(v) {
    if (v.price > 0) {
      try { return (v.price / 100).toLocaleString() + '৳'; } catch (e) { /* noop */ }
    }
    return (v.pf || '').replace(' BDT', '৳').replace('BDT', '৳');
  }

  /* Show the taka sign instead of the BDT code on theme-rendered prices.
     Only touches known price elements (never size labels like "BD 10") and
     only while the active currency is BDT. */
  var TAKA_SELECTORS = '.price-item, .gtj-ctl__price, .gtj-wl-item__price, .cart-item__price-wrapper .price, .totals__total-value';

  function takaSweep() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active && window.Shopify.currency.active !== 'BDT') return;
    var els = document.querySelectorAll(TAKA_SELECTORS);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.textContent.indexOf('BDT') === -1) continue;
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        if (node.textContent.indexOf('BDT') !== -1) {
          node.textContent = node.textContent.replace(' BDT', '৳').replace('BDT', '৳');
        }
      }
    }
  }

  var takaTimer = null;

  function watchTaka() {
    takaSweep();
    var mo = new MutationObserver(function () {
      if (takaTimer) return;
      takaTimer = setTimeout(function () {
        takaTimer = null;
        takaSweep();
      }, 120);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function renderRecentlyViewed(views) {
    var host = document.querySelector('[data-gtj-recent-row]');
    if (!host) return;
    var section = host.closest('.gtj-recent');
    var currentHandle = '';
    var pdpEl = document.getElementById('gtj-journey-product');
    if (pdpEl) {
      try { currentHandle = JSON.parse(pdpEl.textContent).handle || ''; } catch (e) { /* noop */ }
    }
    var wl = wishlistHandles();
    var list = [];
    for (var i = 0; i < views.length; i++) {
      if (views[i].h !== currentHandle && views[i].img) list.push(views[i]);
      if (list.length >= 10) break;
    }
    if (list.length < 2) {
      if (section) section.setAttribute('hidden', '');
      return;
    }
    var frag = document.createDocumentFragment();
    for (var j = 0; j < list.length; j++) {
      var v = list[j];
      var card = document.createElement('div');
      card.className = 'gtj-recent__card';

      var media = document.createElement('div');
      media.className = 'gtj-recent__media';
      var link = document.createElement('a');
      link.href = v.u;
      link.setAttribute('aria-label', v.t);
      var img = document.createElement('img');
      img.src = v.img || '';
      img.alt = v.t;
      img.loading = 'lazy';
      img.width = 360;
      img.height = 360;
      link.appendChild(img);
      media.appendChild(link);

      /* + quick add: reuses the delegated handlers in gtj-card-extras.js.
         Single available variant -> instant add; multi-option -> stepped
         chip picker; no stored variant data -> navigates to the product. */
      var plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'gtj-plus';
      plus.setAttribute('aria-label', 'Add to cart');
      plus.innerHTML = PLUS_SVG;
      var vs = v.vs;
      if (vs && vs.variants && vs.variants.length === 1 && vs.variants[0].a) {
        plus.setAttribute('data-variant-id', String(vs.variants[0].id));
      } else if (vs && vs.variants && vs.variants.length > 1 && vs.names && vs.names.length) {
        var quick = document.createElement('div');
        quick.className = 'gtj-quick gtj-quick--multi';
        quick.innerHTML = '<span class="gtj-quick__label"></span><div class="gtj-quick__sizes"></div>';
        var json = document.createElement('script');
        json.type = 'application/json';
        json.className = 'gtj-quick-json';
        json.textContent = JSON.stringify({ names: vs.names, variants: vs.variants });
        quick.appendChild(json);
        media.appendChild(quick);
      } else {
        plus.setAttribute('data-url', v.u);
      }
      var bm = document.createElement('button');
      bm.type = 'button';
      bm.className = 'gtj-bookmark gtj-recent__bm' + (wl[v.h] ? ' is-saved' : '');
      bm.setAttribute('data-handle', v.h);
      bm.setAttribute('aria-label', 'Save to wishlist');
      bm.innerHTML = BM_OUTLINE + BM_FILL;
      media.appendChild(bm);
      media.appendChild(plus);
      card.appendChild(media);

      var name = document.createElement('a');
      name.className = 'gtj-recent__name';
      name.href = v.u;
      name.textContent = v.t;
      card.appendChild(name);

      var price = document.createElement('span');
      price.className = 'gtj-recent__price';
      price.textContent = fmtMoney(v);
      card.appendChild(price);

      frag.appendChild(card);
    }
    host.innerHTML = '';
    host.appendChild(frag);
    if (section) {
      section.setAttribute('data-gtj-count', String(list.length));
      section.removeAttribute('hidden');
      wireRecentArrows(section, host);
    }
    debugLog('Recently viewed rendered (' + list.length + ' items)');
  }

  /* Menu drawer "Most Wanted" chips: server renders bestsellers; for
     returning visitors with 3+ viewed products, swap in their own picks. */
  function personalizeMenuChips(views) {
    var wrap = document.querySelector('[data-gtj-menu-chips]');
    if (!wrap) return;
    var list = [];
    for (var i = 0; i < views.length; i++) {
      if (views[i].img && views[i].t) list.push(views[i]);
      if (list.length >= 6) break;
    }
    if (list.length < 3) return;
    var frag = document.createDocumentFragment();
    for (var j = 0; j < list.length; j++) {
      var v = list[j];
      var a = document.createElement('a');
      a.className = 'gtj-md__chip';
      a.href = v.u;
      var img = document.createElement('img');
      img.src = v.img;
      img.alt = v.t;
      img.loading = 'lazy';
      img.width = 80;
      img.height = 80;
      a.appendChild(img);
      var span = document.createElement('span');
      span.textContent = v.t;
      a.appendChild(span);
      frag.appendChild(a);
    }
    wrap.innerHTML = '';
    wrap.appendChild(frag);
    var title = document.querySelector('[data-gtj-menu-chips-title]');
    if (title) title.textContent = 'For You';
    debugLog('Menu chips personalized (' + list.length + ')');
  }

  function wireRecentArrows(section, row) {
    var prev = section.querySelector('[data-gtj-recent-prev]');
    var next = section.querySelector('[data-gtj-recent-next]');
    var nav = section.querySelector('.gtj-recent__nav');
    if (!prev || !next || !nav) return;

    function sync() {
      var overflow = row.scrollWidth > row.clientWidth + 2;
      nav.style.visibility = overflow ? '' : 'hidden';
      if (row.scrollLeft <= 2) prev.setAttribute('disabled', '');
      else prev.removeAttribute('disabled');
      if (row.scrollLeft >= row.scrollWidth - row.clientWidth - 2) next.setAttribute('disabled', '');
      else next.removeAttribute('disabled');
    }
    function step(dir) {
      row.scrollBy({ left: dir * Math.max(row.clientWidth - 120, 200), behavior: 'smooth' });
    }
    if (!section.__gtjArrows) {
      section.__gtjArrows = true;
      prev.addEventListener('click', function () { step(-1); });
      next.addEventListener('click', function () { step(1); });
      row.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
    }
    sync();
    /* image loads can change scrollWidth after render */
    setTimeout(sync, 600);
  }

  /* ---------- debug panel ---------- */

  var debugLines = [];

  function debugLog(msg) {
    if (!DEBUG) return;
    debugLines.push(msg);
    var box = document.getElementById('gtj-debug-actions');
    if (box) box.textContent = debugLines.join(' | ');
  }

  function debugBadge(el, score) {
    var badge = document.createElement('span');
    badge.textContent = score;
    badge.style.cssText = 'position:absolute;top:4px;right:4px;z-index:9;background:#000;color:#fff;font-size:10px;line-height:1;padding:4px 6px;border-radius:6px;opacity:.85;pointer-events:none;';
    var target = el.querySelector('.card-wrapper') || el;
    if (window.getComputedStyle(target).position === 'static') target.style.position = 'relative';
    target.appendChild(badge);
  }

  function topEntries(map, n) {
    var arr = [];
    for (var k in map) {
      if (Object.prototype.hasOwnProperty.call(map, k)) arr.push([k, map[k]]);
    }
    arr.sort(function (a, b) { return b[1] - a[1]; });
    var out = [];
    for (var i = 0; i < arr.length && i < n; i++) {
      out.push(arr[i][0] + ' (' + (Math.round(arr[i][1] * 10) / 10) + ')');
    }
    return out.length ? out.join(', ') : 'none yet';
  }

  function renderDebugPanel() {
    var c = ctx();
    var panel = document.createElement('div');
    panel.id = 'gtj-debug-panel';
    panel.style.cssText = 'position:fixed;bottom:12px;left:12px;z-index:99999;max-width:320px;max-height:45vh;overflow:auto;background:rgba(0,0,0,.88);color:#fff;font:11px/1.5 monospace;padding:12px 14px;border-radius:10px;';
    var viewsTxt = [];
    for (var i = 0; i < c.data.views.length && i < 8; i++) {
      var v = c.data.views[i];
      viewsTxt.push(v.t + ' x' + (v.n || 0) + (v.iw ? ' +i' + v.iw : ''));
    }
    panel.innerHTML = '<strong>GTJ JOURNEY DEBUG</strong><br><br><strong>Viewed (' + c.data.views.length + '):</strong><br>';
    var vt = document.createElement('div');
    vt.textContent = viewsTxt.length ? viewsTxt.join(', ') : 'nothing yet - browse some products';
    panel.appendChild(vt);
    var pr = document.createElement('div');
    pr.innerHTML = '<br><strong>Top types:</strong> ';
    panel.appendChild(pr);
    var t1 = document.createElement('span');
    t1.textContent = topEntries(c.profile.types, 3);
    pr.appendChild(t1);
    var pr2 = document.createElement('div');
    pr2.innerHTML = '<strong>Top tags:</strong> ';
    panel.appendChild(pr2);
    var t2 = document.createElement('span');
    t2.textContent = topEntries(c.profile.tags, 5);
    pr2.appendChild(t2);
    var pm = document.createElement('div');
    pm.innerHTML = '<strong>Median price:</strong> ';
    panel.appendChild(pm);
    var t3 = document.createElement('span');
    t3.textContent = c.profile.medianPrice ? (c.profile.medianPrice / 100) + ' BDT' : 'n/a';
    pm.appendChild(t3);
    var act = document.createElement('div');
    act.innerHTML = '<br><strong>Actions:</strong> <span id="gtj-debug-actions">none yet</span>';
    panel.appendChild(act);
    var reset = document.createElement('button');
    reset.textContent = 'RESET DATA';
    reset.style.cssText = 'margin-top:8px;background:#fff;color:#000;border:0;border-radius:6px;padding:4px 8px;font:10px monospace;cursor:pointer;';
    reset.addEventListener('click', function () {
      try { window.localStorage.removeItem(KEY); } catch (e) { /* noop */ }
      window.location.reload();
    });
    panel.appendChild(reset);
    document.body.appendChild(panel);
    if (debugLines.length) {
      var box = document.getElementById('gtj-debug-actions');
      if (box) box.textContent = debugLines.join(' | ');
    }
  }

  /* ---------- boot ---------- */

  function init() {
    trackProductView();
    hookAddToCart();
    watchTaka();
    var data = load();
    renderRecentlyViewed(data.views);
    personalizeMenuChips(data.views);
    if (data.views.length > 0) {
      watchRecommendations();
      watchCartDrawer();
    }
    if (DEBUG) renderDebugPanel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
